import {
  useState,
  useCallback,
  useRef,
  useLayoutEffect,
  useEffect,
  useMemo,
} from "react";
import { Reorder } from "framer-motion";
import VerticalCarousel from "@/components/carousels/VerticalCarousel";
import { useLiveWireStore } from "@/stores/livewire";
import { useShallow } from "zustand/react/shallow";
import { HOST_URI } from "@/services/approveshield/constants/uri";

const itemSize = 65;
const headerSize = 60;

const SentToQaProgressWidget = ({ refreshInterval = 60000 }) => {
  const [loading, setLoading] = useState(false);

  const [analystDaily, analystWeekly, fetchDaily, fetchWeekly] =
    useLiveWireStore(
      useShallow((state) => [
        state.analystDaily,
        state.analystWeekly,
        state.getDailyAnalystStats,
        state.getWeeklyAnalystStats,
      ])
    );

  // Initial data fetch
  useEffect(() => {
    setLoading(true);
    fetchDaily()
      .then(() => fetchWeekly())
      .finally(() => setLoading(false));
  }, []);

  // Refresh data at specified interval
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchDaily();
      fetchWeekly();
    }, refreshInterval);
    // Clear the interval on component unmount
    return () => clearInterval(intervalId);
  }, [refreshInterval, fetchDaily, fetchWeekly]);

  const data = useMemo(() => {
    return analystDaily.map((daily) => {
      const weekly = analystWeekly.find((w) => w.id === daily.id) || {};
      return {
        ...daily,
        weekly,
      };
    });
  }, [analystDaily, analystWeekly]);

  const [chunkSize, setChunkSize] = useState(8);
  const elementRef = useRef(null);

  useLayoutEffect(() => {
    const updateHeight = () => {
      if (elementRef.current) {
        setChunkSize(
          Math.floor((elementRef.current.clientHeight - headerSize) / itemSize)
        );
      }
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  return (
    <div className="sent-to-qa-today widget" ref={elementRef}>
      <div className="card card-rounded w-100 mb-0">
        <div className="p-3 pb-1 d-flex justify-content-between">
          <div className="d-flex align-items-center">
            <span className="bi bi-patch-check-fill text-orange me-2 fs-24"></span>
            <span className="text-black fw-bold fs-18">Sent to QA</span>
          </div>
        </div>
        <div className="card-body px-3 py-0 overflow-hidden position-relative">
          {loading && data.length === 0 && (
            <div className="spinner-border text-orange">
              <span className="visually-hidden">Loading...</span>
            </div>
          )}
          <div className="position-absolute w-100 h100">
            <AnalystProgressTable chunkSize={chunkSize} data={data} />
          </div>
        </div>
      </div>
    </div>
  );
};


const AnalystProgressTable = ({ data, chunkSize = 12 }) => {
  const chunks = [];
  for (let i = 0; i < data.length; i += chunkSize) {
    chunks.push(data.slice(i, i + chunkSize));
  }

  return (
    <div className="container-fluid">
      <AnalystListHeader />
      <VerticalCarousel
        options={{
          slideHeight: `${itemSize * chunkSize + 10}px`,
        }}
        slides={chunks.map((chunk, index) => (
          <Reorder.Group
            axis="y"
            values={chunk}
            as="div"
            className="table-group"
          >
            {chunk.map((item) => (
              <Reorder.Item
                key={item.name}
                value={item}
                dragListener={false}
                as="div"
              >
                <AnalystListItem
                  id={item.id}
                  avatar={item.avatar}
                  name={item.name}
                  accuracy={item.accuracy || 0}
                  today={item.sent_to_qa}
                  kickbacks={item.kickbacks}
                  streak={item.weekly?.streak || 0}
                  sla={item.weekly?.sla || 0}
                />
              </Reorder.Item>
            ))}
          </Reorder.Group>
        ))}
      ></VerticalCarousel>
    </div>
  );
};

const AnalystListHeader = () => {
  return (
    <div className="analyst-list-header row text-dark fw-bold mb-1">
      <div className="col col-2">Name</div>
      <div className="col">Daily</div>
      <div className="col col-1 text-center">SLA</div>
      <div className="col col-1 text-center">Streak</div>
      <div className="col col-1 text-center">QA Returns</div>
      <div className="col col-1 text-center">Accuracy</div>
    </div>
  );
};

const getColor = (count) => {
  if (count > 29) {
    return "purple-gradient";
  } else if (count > 23) {
    return "purple";
  } else if (count > 15) {
    return "info";
  } else if (count > 12) {
    return "success";
  } else if (count >= 9) {
    return "orange";
  } else {
    return "danger";
  }
};

const AnalystListItem = ({ id, name, today, accuracy, avatar, kickbacks, streak, sla }) => {
  return (
    <div className="analyst-list-item row text-dark pt-1 border-bottom border-light">
      <div className="col col-2 py-2">
        <div className="d-flex flex-row align-items-center gap-2">
          <span className="avatar avatar-md avatar-rounded bg-orange">
            {avatar ? (
              <img src={`${HOST_URI}/v1/users/${id}/avatar`} alt="" />
            ) : (
              <span className="text-white fw-bold">
                {name?.charAt(0).toUpperCase()}
                {name?.split(" ")[1]?.charAt(0).toUpperCase() || ""}
              </span>
            )}
          </span>
          <span className="ms-2 text-nowrap fs-16 fw-semibold text-truncate">
            {name}
          </span>
        </div>
      </div>
      <div className="col ">
        <div className="mt-3">
          <div
            className={`progress progress-xl progress-animate custom-progress-4 ${getColor(
              today
            )}`}
            role="progressbar"
            aria-valuenow={today / 15}
            aria-valuemin={0}
            aria-valuemax="100"
          >
            <div
              className={`progress-bar bg-${getColor(today)}`}
              style={{ width: `${(today / 16) * 100}%` }}
            >
              <div className="progress-bar-label text-white">{today}</div>
            </div>
          </div>
        </div>
      </div>
      <div className="col col-1 py-2 text-center">
        <div className="fw-bold">{sla ? `${sla} days` : "-"}</div>
      </div>
      <div className="col col-1 py-2 text-center">
        <div className="fw-bold">{streak}</div>
      </div>
      <div className="col col-1 py-2 text-center">
        <div className="fw-bold">{kickbacks}</div>
      </div>
      <div className="col col-1 py-2 text-center">
        <div className="fw-bold">{accuracy}%</div>
      </div>
    </div>
  );
};

export default SentToQaProgressWidget;
